type PreviewEnumOption<T extends string> = {
  value: T;
  label: string;
};

export type GenderCode = 'male' | 'female' | 'other' | 'unknown';
export type VisualAgeCode =
  | 'baby'
  | 'child'
  | 'teenager'
  | 'young_adult'
  | 'middle_aged'
  | 'elderly'
  | 'unknown';
export type RaceCode =
  | 'human'
  | 'animal'
  | 'plant'
  | 'alien'
  | 'mythical_creature'
  | 'machine'
  | 'other'
  | 'unknown';
export type TimeOfDayCode = 'day' | 'night' | 'dawn' | 'dusk' | 'unknown';
export type WeatherCode = 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'foggy' | 'windy' | 'unknown';
export type SeasonCode = 'spring' | 'summer' | 'autumn' | 'winter' | 'unknown';

function createOptions<const T extends string>(
  labels: Record<T, string>
): Array<PreviewEnumOption<T>> {
  return (Object.keys(labels) as T[]).map((value) => ({
    value,
    label: labels[value],
  }));
}

function getEnumLabel<T extends string>(
  labels: Record<T, string>,
  value?: string | null,
  fallback = '-'
) {
  if (!value) {
    return fallback;
  }

  return labels[value as T] ?? value;
}

export const GENDER_LABELS: Record<GenderCode, string> = {
  male: '男',
  female: '女',
  other: '其他',
  unknown: '未知',
};

export const VISUAL_AGE_LABELS: Record<VisualAgeCode, string> = {
  baby: '婴儿',
  child: '儿童',
  teenager: '少年',
  young_adult: '青年',
  middle_aged: '中年',
  elderly: '老年',
  unknown: '未知',
};

export const TIME_OF_DAY_LABELS: Record<TimeOfDayCode, string> = {
  day: '白天',
  night: '夜晚',
  dawn: '黎明',
  dusk: '黄昏',
  unknown: '未知',
};

export const RACE_LABELS: Record<RaceCode, string> = {
  human: '人类',
  animal: '动物',
  plant: '植物',
  alien: '外星生物',
  mythical_creature: '神话生物',
  machine: '机械实体',
  other: '其他',
  unknown: '未知',
};

export const WEATHER_LABELS: Record<WeatherCode, string> = {
  sunny: '晴天',
  cloudy: '多云',
  rainy: '雨天',
  snowy: '雪天',
  foggy: '雾天',
  windy: '大风',
  unknown: '未知',
};

export const SEASON_LABELS: Record<SeasonCode, string> = {
  spring: '春季',
  summer: '夏季',
  autumn: '秋季',
  winter: '冬季',
  unknown: '未知',
};

export const GENDER_OPTIONS = createOptions(GENDER_LABELS);
export const VISUAL_AGE_OPTIONS = createOptions(VISUAL_AGE_LABELS);
export const RACE_OPTIONS = createOptions(RACE_LABELS);
export const TIME_OF_DAY_OPTIONS = createOptions(TIME_OF_DAY_LABELS);
export const WEATHER_OPTIONS = createOptions(WEATHER_LABELS);
export const SEASON_OPTIONS = createOptions(SEASON_LABELS);

export function getGenderLabel(value?: string | null) {
  return getEnumLabel(GENDER_LABELS, value);
}

export function getVisualAgeLabel(value?: string | null) {
  return getEnumLabel(VISUAL_AGE_LABELS, value);
}

export function getTimeOfDayLabel(value?: string | null) {
  return getEnumLabel(TIME_OF_DAY_LABELS, value);
}

export function getRaceLabel(value?: string | null) {
  return getEnumLabel(RACE_LABELS, value);
}

export function getWeatherLabel(value?: string | null) {
  return getEnumLabel(WEATHER_LABELS, value);
}

export function getSeasonLabel(value?: string | null) {
  return getEnumLabel(SEASON_LABELS, value);
}

// 字段名称与下方的枚举值翻译分开，引用标题不能插入枚举对象。
export const ASSET_METADATA_FIELD_LABELS: Record<string, string> = {
  personality: '性格',
  persona: '人物设定',
  emotionArc: '情绪弧线',
  narrativeFunction: '叙事作用',
  narrativeSignificance: '叙事作用',
  narrativeNote: '叙事备注',
  narrativePosition: '叙事位置',
  appearance: '外观',
  voiceModel: '音色',
  lighting: '光照',
  lightingDesign: '灯光设计',
  lightingSetup: '灯光设置',
  colorTone: '色调',
  mood: '情绪氛围',
  spatialFeel: '空间感',
  material: '材质',
  size: '尺寸',
  color: '颜色',
  style: '样式',
  movieRoleType: '电影角色类型',
  moviePropType: '影视道具分类',
  characterArc: '角色弧线',
  keyTrait: '标志性特征',
  relationshipMap: '人物关系',
  adRoleType: '广告角色类型',
  ageRange: '年龄范围',
  makeupStyle: '妆容风格',
  sceneType: '场景类型',
  adPropType: '广告道具类型',
  productState: '产品状态',
  shortRoleType: '短视频角色类型',
  memorablePoint: '记忆点',
  verticalFit: '竖屏适配',
  shortPropType: '短视频道具类型',
  showAngles: '展示角度',
  priceRevealTiming: '价格揭示时机',
  seriesRoleType: '剧集角色类型',
  arcProgress: '弧光进度',
  episodeFunction: '本集作用',
  emotionalIn: '入场情绪',
  emotionalOut: '退场情绪',
  continuity: '连续性',
  setType: '场景复用类型',
  plotLine: '剧情线',
  episodeSegment: '集内段落',
  continuityNote: '连续性备注',
  seriesPropType: '剧集道具类型',
  previousAppearance: '前次出现',
  futurePayoff: '后续回收',
  isClue: '线索道具',
  coreIdentity: '核心身份',
  visualKeyword: '视觉关键词',
  oneLiner: '一句话描述',
  coreMood: '核心氛围',
  dominantColor: '主色调',
  visualAnchor: '视觉锚点',
  referenceHint: '视觉参考提示',
  conceptPropType: '概念道具类型',
  visualImpact: '视觉冲击力',
  animeRoleType: '动漫角色类型',
  artStyle: '艺术风格',
  chibiForm: 'Q版形态',
  signatureEffect: '标志性特效',
  expressionPack: '表情集',
  actionStyle: '动作风格',
  characterVariants: '角色形态',
  worldType: '世界类型',
  colorPalette: '配色方案',
  dynamicElements: '动态元素',
  perspectiveStyle: '透视风格',
  animePropType: '动漫道具类型',
  transformStates: '变形状态',
  effect: '特效',
  particleOrLighting: '粒子与光效',
};

// 扩展字段必须按字段解释，不能将相同单词跨字段统一替换。
export const ASSET_METADATA_LABELS: Record<string, Record<string, string>> = {
  sceneType: {
    hero_scene: '产品展示主场景',
    usage_scene: '使用场景',
    atmosphere_scene: '氛围场景',
    comparison_scene: '对比场景',
    indoor_setup: '室内布景/棚拍',
    outdoor: '户外场景',
    location_feature: '特色场景',
    green_screen: '绿幕/虚拟背景',
    car_interview: '车内拍摄',
  },
  narrativePosition: {
    opening: '开场',
    inciting_incident: '激励事件',
    midpoint: '中点转折',
    climax: '高潮',
    resolution: '结局',
    transition: '过渡',
  },
  productState: {
    packaging: '完整包装',
    unboxed: '开箱状态',
    in_use: '使用中',
    result: '效果展示',
    comparison: '对比状态',
  },
  visualImpact: { high: '高', medium: '中', low: '低' },
};

const LOCATION_LABELS: Record<string, string> = {
  interior: '内景',
  indoor: '内景',
  exterior: '外景',
  outdoor: '外景',
  unknown: '未知',
};
const PROP_TYPE_LABELS: Record<string, string> = {
  interactive: '互动道具',
  set_dressing: '布景道具',
  setDressing: '布景道具',
};

export function getAssetMetadataLabel(field: string, value: string): string {
  const labels = Object.prototype.hasOwnProperty.call(ASSET_METADATA_LABELS, field)
    ? ASSET_METADATA_LABELS[field]
    : undefined;
  return labels && Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : value;
}

export function getLocationLabel(value?: string | null): string | undefined {
  const normalized = value?.trim();
  if (!normalized) return undefined;
  const key = normalized.toLowerCase();
  return Object.prototype.hasOwnProperty.call(LOCATION_LABELS, key)
    ? LOCATION_LABELS[key]
    : normalized;
}

export function getPropTypeLabel(value?: string | null): string | undefined {
  if (!value) return undefined;
  return Object.prototype.hasOwnProperty.call(PROP_TYPE_LABELS, value)
    ? PROP_TYPE_LABELS[value]
    : value;
}
